import { useState, useCallback, useMemo } from "react";
import { format, startOfMonth, addMonths, subMonths, eachMonthOfInterval, startOfYear, endOfYear, subYears, isSameMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

export function useMonthSelector() {
  const [selectedDate, setSelectedDate] = useState(() => startOfMonth(new Date()));
  const [isOpen, setIsOpen] = useState(false);

  const monthName = useMemo(() => {
    const formatted = format(selectedDate, "MMMM yyyy", { locale: ptBR });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }, [selectedDate]);

  const monthRange = useMemo(() => {
    const start = format(selectedDate, "yyyy-MM-01");
    const end = format(addMonths(selectedDate, 1), "yyyy-MM-01");
    return { start, end };
  }, [selectedDate]);

  const months = useMemo(() => {
    const now = new Date();
    const currentYearMonths = eachMonthOfInterval({
      start: startOfYear(now),
      end: endOfYear(now),
    });
    const lastYearMonths = eachMonthOfInterval({
      start: startOfYear(subYears(now, 1)),
      end: endOfYear(subYears(now, 1)),
    });

    return [...currentYearMonths, ...lastYearMonths].sort((a, b) => b.getTime() - a.getTime());
  }, []);

  const MonthSelector = useCallback(() => (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2 bg-card border-border hover:bg-white/5 h-11 px-4 min-w-[180px] justify-between shadow-sm"
        >
          <div className="flex items-center gap-2">
            <CalendarIcon size={18} className="text-muted-foreground" />
            <span className="text-foreground font-semibold">
              {monthName}
            </span>
          </div>
          <ChevronDown size={16} className="text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0 bg-card border-border shadow-xl" align="start">
        <ScrollArea className="h-[300px]">
          <div className="p-1">
            {months.map((month) => {
              const label = format(month, "MMMM yyyy", { locale: ptBR });
              const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
              const isSelected = isSameMonth(month, selectedDate);

              return (
                <Button
                  key={month.toISOString()}
                  variant="ghost"
                  className={cn(
                    "w-full justify-start font-medium text-sm h-10 px-3",
                    isSelected ? "bg-primary/10 text-primary hover:bg-primary/20" : "text-foreground hover:bg-white/5"
                  )}
                  onClick={() => {
                    setSelectedDate(month);
                    setIsOpen(false);
                  }}
                >
                  {capitalizedLabel}
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  ), [monthName, months, selectedDate, isOpen]);

  return {
    selectedDate,
    setSelectedDate,
    monthRange,
    MonthSelector
  };
}
